import { AdapterProtocol } from "../flatbuffers/generated/archive/adapter-protocol";

/**
 * Channel "type" as surfaced to the operator — a first-class routing
 * classification presented like SINGLE, backed by the authoritative
 * `routable` boolean on the channel. The bool stays the wire/DB
 * representation (an additive trailing FlatBuffer field, absent === false) so
 * dual-schema compatibility and legacy producers are unaffected; only the UI
 * promotes it to a selectable type.
 *
 *   SINGLE   -> routable=false -> VTS uses the direct vendor path.
 *   ROUTABLE -> routable=true  -> VTS routes the channel through the
 *               CGA / LobbyVision / LobbyVision2 accumulation map keyed on
 *               routable_socket_address (one socket dispatches to many banks).
 */
export const CHANNEL_TYPE_SINGLE = "SINGLE";
export const CHANNEL_TYPE_ROUTABLE = "ROUTABLE";
export type ChannelRoutingType = typeof CHANNEL_TYPE_SINGLE | typeof CHANNEL_TYPE_ROUTABLE;

/** Map the authoritative `routable` bool to the operator-facing channel type. */
export function channelTypeFromRoutable(routable: boolean): ChannelRoutingType {
  return routable ? CHANNEL_TYPE_ROUTABLE : CHANNEL_TYPE_SINGLE;
}

/** Map the operator-facing channel type back to the `routable` bool. */
export function routableFromChannelType(value: string): boolean {
  return value === CHANNEL_TYPE_ROUTABLE;
}

/**
 * Adapter protocols VTS always routes through a routed accumulation map: CGA
 * and LobbyVision cannot run the direct path at all, and LobbyVision2 is
 * routed whenever its channel is marked routable. Derived from the generated
 * `AdapterProtocol` enum names (reverse lookup) rather than a hand-rolled
 * string list, so a future schema addition can't silently drift the set — the
 * same defense `ADAPTER_PROTOCOL_NUMERIC_TO_STRING` uses in StepBanks.
 *
 * Deliberately NOT the `LN1_PROTO_SET` used for offset mapping: that set is
 * wrong for routing (it includes LN1/RVU, which are direct, and omits
 * LobbyVision2, which is routable).
 */
export const ROUTABLE_PROTOCOLS: ReadonlySet<string> = new Set<string>([
  AdapterProtocol[AdapterProtocol.CGA],
  AdapterProtocol[AdapterProtocol.LOBBYVISION],
  AdapterProtocol[AdapterProtocol.LOBBYVISION2],
]);

/** True when the adapter protocol (any case) is one VTS always routes. */
export function isRoutableProtocol(protocol: string): boolean {
  return ROUTABLE_PROTOCOLS.has(protocol.toUpperCase());
}

/**
 * Which mismatch warning (if any) applies when a channel of the given
 * routability is paired with an adapter of the given protocol on a bank.
 *
 * - NON_ROUTABLE_ON_ROUTED_ADAPTER: a Single channel on a CGA/LV/LV2 adapter.
 *   VTS force-routes CGA/LobbyVision (they cannot run direct) and falls back
 *   to the direct path for LobbyVision2 — usually not what the operator wants.
 * - ROUTABLE_ON_DIRECT_ADAPTER: a Routable channel on any other adapter.
 *   Allowed; VTS ignores routing and uses the direct vendor path.
 *
 * Returns null when the two are consistent or no adapter is selected yet.
 */
export type RoutableWarningKind =
  "NON_ROUTABLE_ON_ROUTED_ADAPTER" | "ROUTABLE_ON_DIRECT_ADAPTER" | null;

export function routableMismatchWarning(
  channelRoutable: boolean,
  protocol: string,
): RoutableWarningKind {
  const proto = protocol.toUpperCase();
  if (proto === "") return null;
  if (isRoutableProtocol(proto) && !channelRoutable) {
    return "NON_ROUTABLE_ON_ROUTED_ADAPTER";
  }
  if (!isRoutableProtocol(proto) && channelRoutable) {
    return "ROUTABLE_ON_DIRECT_ADAPTER";
  }
  return null;
}
